import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { authorizedFetch } from '@/lib/api'

export type ClassifierOutputContract = {
  output_json_schema: {
    type?: string
    properties?: Record<string, unknown>
    required?: string[]
    [key: string]: unknown
  }
  field_help: Record<string, string>
  enum_labels: Record<string, Record<string, string>>
  has_followup_guidance: boolean
  mirror_targets: string[]
}

export type ClassifierCatalogEntry = {
  profile_id: string
  label: string
  capability_class: string
  trigger: string
  debounce_seconds: number
  allowlisted: boolean
  corpus_id: string | null
  corpus_status: string | null
  current_version: number | null
  authoring_mode?: string
  editable_fields?: string[]
  contract?: ClassifierOutputContract | null
}

export type ClassifierRevision = {
  id: string
  version: number
  payload: Record<string, unknown>
  provenance: string
  change_summary?: string
  effective_from?: string
}

export type ClassifierCorpus = {
  id: string
  client_config: string
  profile_id: string
  slug: string
  label: string
  capability_class: string
  status: string
  current_revision: ClassifierRevision | null
  revisions?: ClassifierRevision[]
  preview_transcript?: string
  authoring_mode?: string
  editable_fields?: string[]
  contract?: ClassifierOutputContract | null
}

export function useClassifierCatalog() {
  return useQuery({
    queryKey: ['admin-classifier-catalog'],
    queryFn: async (): Promise<ClassifierCatalogEntry[]> => {
      const res = await authorizedFetch('/api/classifiers/admin/catalog')
      if (!res.ok) throw new Error('Failed to load classifier catalog')
      return res.json()
    },
  })
}

export function useClassifierCorpus(corpusId: string | undefined) {
  return useQuery({
    queryKey: ['admin-classifier-corpus', corpusId],
    enabled: Boolean(corpusId),
    queryFn: async (): Promise<ClassifierCorpus> => {
      const res = await authorizedFetch(`/api/classifiers/admin/corpora/${corpusId}`)
      if (!res.ok) throw new Error('Failed to load classifier corpus')
      return res.json()
    },
  })
}

export function useEnsureClassifierCorpus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (profile_id: string) => {
      const res = await authorizedFetch('/api/classifiers/admin/corpora', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profile_id }),
      })
      if (!res.ok) {
        const text = await res.text()
        throw new Error(text || 'Failed to create corpus')
      }
      return res.json() as Promise<ClassifierCorpus>
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-classifier-catalog'] })
    },
  })
}

export function usePublishClassifierRevision(corpusId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: {
      payload: Record<string, unknown>
      change_summary?: string
    }) => {
      const res = await authorizedFetch(
        `/api/classifiers/admin/corpora/${corpusId}/revisions`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      )
      if (!res.ok) {
        const text = await res.text()
        throw new Error(text || 'Failed to publish revision')
      }
      return res.json() as Promise<ClassifierCorpus>
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-classifier-corpus', corpusId] })
      void qc.invalidateQueries({ queryKey: ['admin-classifier-catalog'] })
    },
  })
}

export function useClassifierPreview() {
  return useMutation({
    mutationFn: async (body: {
      corpus_id?: string
      profile_id?: string
      payload?: Record<string, unknown>
      transcript: string
      prior_output?: Record<string, unknown>
    }) => {
      const res = await authorizedFetch('/api/classifiers/admin/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!res.ok) {
        const text = await res.text()
        throw new Error(text || 'Preview failed')
      }
      return res.json() as Promise<{
        profile_id: string
        output: Record<string, unknown>
        used_payload: Record<string, unknown>
      }>
    },
  })
}
